/**
 * Fund List Item Component
 *
 * Displays a ranked fund card with:
 * - Rank position number
 * - Fund name
 * - Score badge (with color coding based on score)
 * - Rating status indicator
 * - Returns row: 1Y, 3Y, 5Y percentages
 * - Fund metrics: AUM, expense ratio, exit load
 * - Pressable/touchable for navigation
 */

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINRCompact, formatPercent } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import type { FundRating } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Fund data for the list item display
 */
export interface FundListItemData {
  /** Unique identifier for the fund (ISIN) */
  isin: string;
  /** Rank position in the category (1 = best) */
  rank: number;
  /** Fund name */
  name: string;
  /** Fund category (e.g., "equity-large-cap") */
  category: string;
  /** AMC name */
  amcName: string;
  /** Fund rating based on rank percentile */
  rating: FundRating;
  /** Composite score (0-100) combining consistency, recency, volatility */
  score: number;
  /** 1 Year returns percentage */
  returns1Y: number;
  /** 3 Year returns percentage */
  returns3Y: number;
  /** 5 Year returns percentage */
  returns5Y: number;
  /** Assets Under Management in INR (crores) */
  aum: number;
  /** Expense ratio percentage */
  expenseRatio: number;
  /** Exit load description (e.g., "1% if redeemed within 1 year") */
  exitLoad: string;
}

/**
 * Props for the FundListItem component
 */
export interface FundListItemProps {
  /** Fund data */
  data: FundListItemData;
  /** Click handler for navigation */
  onClick?: (isin: string) => void;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the rank badge prominently */
  showRank?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Status configuration with colors and labels for fund ratings
 */
const RATING_CONFIG: Record<
  FundRating,
  {
    label: string;
    badgeBg: string;
    badgeText: string;
    borderColor: string;
  }
> = {
  "in-form": {
    label: "In-Form",
    badgeBg: "bg-emerald-100 dark:bg-emerald-900/30",
    badgeText: "text-emerald-700 dark:text-emerald-400",
    borderColor: "border-emerald-200 dark:border-emerald-800",
  },
  "on-track": {
    label: "On-Track",
    badgeBg: "bg-sky-100 dark:bg-sky-900/30",
    badgeText: "text-sky-700 dark:text-sky-400",
    borderColor: "border-sky-200 dark:border-sky-800",
  },
  "off-track": {
    label: "Off-Track",
    badgeBg: "bg-amber-100 dark:bg-amber-900/30",
    badgeText: "text-amber-700 dark:text-amber-400",
    borderColor: "border-amber-200 dark:border-amber-800",
  },
  "out-of-form": {
    label: "Out-of-Form",
    badgeBg: "bg-rose-100 dark:bg-rose-900/30",
    badgeText: "text-rose-700 dark:text-rose-400",
    borderColor: "border-rose-200 dark:border-rose-800",
  },
};

/**
 * Score badge color based on score value
 * High scores (80+) = green, Medium (50-79) = yellow, Low (<50) = red
 */
function getScoreColor(score: number): {
  bg: string;
  text: string;
  border: string;
} {
  if (score >= 80) {
    return {
      bg: "bg-emerald-100 dark:bg-emerald-900/30",
      text: "text-emerald-700 dark:text-emerald-400",
      border: "border-emerald-200 dark:border-emerald-800",
    };
  }
  if (score >= 50) {
    return {
      bg: "bg-amber-100 dark:bg-amber-900/30",
      text: "text-amber-700 dark:text-amber-400",
      border: "border-amber-200 dark:border-amber-800",
    };
  }
  return {
    bg: "bg-rose-100 dark:bg-rose-900/30",
    text: "text-rose-700 dark:text-rose-400",
    border: "border-rose-200 dark:border-rose-800",
  };
}

/**
 * Returns percentage color based on value
 */
function getReturnsColor(value: number): string {
  if (value > 0) return "text-green-600 dark:text-green-500";
  if (value < 0) return "text-red-600 dark:text-red-500";
  return "text-muted-foreground";
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Rank badge showing position in category
 */
function RankBadge({ rank }: { rank: number }) {
  // Top 3 ranks get special styling
  const isTopThree = rank <= 3;

  return (
    <div
      className={cn(
        "flex items-center justify-center w-10 h-10 rounded-lg font-bold text-lg shrink-0",
        isTopThree
          ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-sm"
          : "bg-muted text-muted-foreground"
      )}
    >
      {rank}
    </div>
  );
}

/**
 * Score badge with color coding
 */
function ScoreBadge({ score }: { score: number }) {
  const colors = getScoreColor(score);
  const clampedScore = Math.max(0, Math.min(100, Math.round(score)));

  return (
    <Badge
      variant="outline"
      className={cn(
        "font-semibold tabular-nums border",
        colors.bg,
        colors.text,
        colors.border
      )}
    >
      {clampedScore}
    </Badge>
  );
}

/**
 * Rating status badge
 */
function RatingBadge({ rating }: { rating: FundRating }) {
  const config = RATING_CONFIG[rating];

  return (
    <Badge
      variant="secondary"
      className={cn(
        "font-medium border text-xs",
        config.badgeBg,
        config.badgeText,
        config.borderColor
      )}
    >
      {config.label}
    </Badge>
  );
}

/**
 * Returns display for a single period
 */
function ReturnCell({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  const color = getReturnsColor(value);

  return (
    <div className="flex flex-col items-center">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <span className={cn("text-sm font-semibold tabular-nums", color)}>
        {formatPercent(value, 1)}
      </span>
    </div>
  );
}

/**
 * Metric item for fund details
 */
function MetricItem({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{label}:</span>
      <span className="text-xs font-medium">{value}</span>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Fund List Item
 *
 * Displays a ranked fund card designed for list views such as category rankings,
 * search results, or recommendations. Includes comprehensive fund metrics and
 * performance data at a glance.
 *
 * @example
 * ```tsx
 * <FundListItem
 *   data={{
 *     isin: "INF200K01RJ0",
 *     rank: 1,
 *     name: "Parag Parikh Flexi Cap Fund - Direct Growth",
 *     category: "equity-flexi-cap",
 *     amcName: "PPFAS Mutual Fund",
 *     rating: "in-form",
 *     score: 94,
 *     returns1Y: 22.78,
 *     returns3Y: 18.45,
 *     returns5Y: 21.32,
 *     aum: 45678,
 *     expenseRatio: 0.63,
 *     exitLoad: "1% if redeemed within 365 days",
 *   }}
 *   onClick={(isin) => navigate(`/funds/${isin}`)}
 * />
 * ```
 */
export function FundListItem({
  data,
  onClick,
  className,
  showRank = true,
}: FundListItemProps) {
  const handleClick = () => {
    onClick?.(data.isin);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Card
      className={cn(
        "w-full overflow-hidden transition-all duration-200",
        onClick && "cursor-pointer hover:shadow-md hover:border-muted-foreground/20",
        className
      )}
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      aria-label={onClick ? `View details for ${data.name}` : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Rank Badge */}
          {showRank && <RankBadge rank={data.rank} />}

          {/* Main Content */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Header Row: Name + Badges */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm leading-tight line-clamp-2">
                  {data.name}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {data.amcName}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <ScoreBadge score={data.score} />
                <RatingBadge rating={data.rating} />
              </div>
            </div>

            {/* Returns Row */}
            <div className="flex items-center justify-between border-y border-border/50 py-2">
              <div className="flex items-center gap-4">
                <ReturnCell label="1Y" value={data.returns1Y} />
                <ReturnCell label="3Y" value={data.returns3Y} />
                <ReturnCell label="5Y" value={data.returns5Y} />
              </div>
              {onClick && (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </div>

            {/* Metrics Row: AUM, Expense Ratio, Exit Load */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <MetricItem label="AUM" value={formatINRCompact(data.aum * 10000000)} />
              <MetricItem label="Expense" value={`${data.expenseRatio.toFixed(2)}%`} />
              <MetricItem label="Exit Load" value={data.exitLoad || "Nil"} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for FundListItem
 */
export function FundListItemSkeleton({
  className,
  showRank = true,
}: {
  className?: string;
  showRank?: boolean;
}) {
  return (
    <Card className={cn("w-full overflow-hidden", className)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Rank Badge Skeleton */}
          {showRank && <Skeleton className="w-10 h-10 rounded-lg shrink-0" />}

          {/* Main Content Skeleton */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Header Row Skeleton */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Skeleton className="h-5 w-10 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </div>

            {/* Returns Row Skeleton */}
            <div className="flex items-center justify-between border-y border-border/50 py-2">
              <div className="flex items-center gap-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <Skeleton className="h-2.5 w-5" />
                    <Skeleton className="h-4 w-12" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-4 w-4 rounded" />
            </div>

            {/* Metrics Row Skeleton */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Skeleton className="h-3 w-10" />
                  <Skeleton className="h-3 w-14" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// List Component
// ============================================================================

/**
 * Props for the fund list
 */
export interface FundListProps {
  /** Array of funds to display */
  funds: FundListItemData[];
  /** Click handler for fund items */
  onFundClick?: (isin: string) => void;
  /** Additional CSS classes */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** Whether to show rank badges */
  showRank?: boolean;
}

/**
 * Fund List
 *
 * Renders a list of fund list items.
 *
 * @example
 * ```tsx
 * <FundList
 *   funds={rankedFunds}
 *   onFundClick={(isin) => navigate(`/funds/${isin}`)}
 *   emptyMessage="No funds found"
 * />
 * ```
 */
export function FundList({
  funds,
  onFundClick,
  className,
  emptyMessage = "No funds found",
  showRank = true,
}: FundListProps) {
  if (funds.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {funds.map((fund) => (
        <FundListItem
          key={fund.isin}
          data={fund}
          onClick={onFundClick}
          showRank={showRank}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton loader for the fund list
 */
export function FundListSkeleton({
  count = 5,
  className,
  showRank = true,
}: {
  count?: number;
  className?: string;
  showRank?: boolean;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {[...Array(count)].map((_, i) => (
        <FundListItemSkeleton key={i} showRank={showRank} />
      ))}
    </div>
  );
}

export default FundListItem;
